import { useCallback } from 'react';
import { useMutation } from '@apollo/client';
// Api
import {
  APPROVE_TICKET_ZERO_TAX,
  SET_TICKET_SALE_STATUS,
} from 'api/ticketing/mutations';
// Types
import {
  ApproveTicketZeroTax,
  ApproveTicketZeroTaxVariables,
} from 'api/ticketing/types/ApproveTicketZeroTax';
import {
  SetTicketSaleStatus,
  SetTicketSaleStatusVariables,
} from 'api/ticketing/types/SetTicketSaleStatus';
import { TicketEventStatus } from 'api/graphql-global-types';

type VersionedEvent = { id: string; version: number };

export type TicketSaleStatus = {
  /** Resolves to the version the next change has to send. */
  approveZeroTax: (event: VersionedEvent) => Promise<number>;
  setStatus: (
    event: VersionedEvent,
    status: TicketEventStatus,
    options?: { approveZeroTaxFirst?: boolean }
  ) => Promise<void>;
  busy: boolean;
};

export const useTicketSaleStatus = (): TicketSaleStatus => {
  const [setSaleStatus, { loading: changingStatus }] = useMutation<
    SetTicketSaleStatus,
    SetTicketSaleStatusVariables
  >(SET_TICKET_SALE_STATUS);
  const [approveTax, { loading: approving }] = useMutation<
    ApproveTicketZeroTax,
    ApproveTicketZeroTaxVariables
  >(APPROVE_TICKET_ZERO_TAX);

  const approveZeroTax = useCallback(
    async (event: VersionedEvent): Promise<number> => {
      const { data } = await approveTax({
        variables: {
          input: { ticketEventId: event.id, expectedVersion: event.version },
        },
      });

      return data?.approveTicketZeroTax.version ?? event.version + 1;
    },
    [approveTax]
  );

  const setStatus = useCallback(
    async (
      event: VersionedEvent,
      status: TicketEventStatus,
      { approveZeroTaxFirst = false } = {}
    ): Promise<void> => {
      const expectedVersion = approveZeroTaxFirst
        ? await approveZeroTax(event)
        : event.version;

      await setSaleStatus({
        variables: {
          input: { ticketEventId: event.id, expectedVersion, status },
        },
      });
    },
    [approveZeroTax, setSaleStatus]
  );

  return { approveZeroTax, setStatus, busy: changingStatus || approving };
};
